import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import { FaBuilding, FaCode, FaGavel, FaHome, FaStore, FaUser } from "react-icons/fa";
import { TiArrowRight, TiArrowUp } from "react-icons/ti";
import { useNavigate } from "react-router-dom";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const SiteMap = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const navigate = useNavigate();

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            delay: index * 0.2,
            scrollTrigger: {
              trigger: section,
              start: "top 80%",
            },
          }
        );
      }
    });

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }

    if (scrollTopRef.current) {
      gsap.fromTo(
        scrollTopRef.current,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          scrollTrigger: {
            trigger: scrollTopRef.current,
            start: "top bottom",
          },
        }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (
      window.innerHeight + window.scrollY >=
      document.body.offsetHeight - 200
    ) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  const handleNavigate = (path) => {
    navigate(path);
  };

  const siteMapLinks = [
    {
      category: "Main Pages",
      icon: <FaHome className="text-cyan-400" />,
      color: "cyan",
      links: [
        { name: "Home", path: "/" },
        { name: "About Us", path: "/about-us" },
        { name: "Services", path: "/services" },
        { name: "Products", path: "/products" },
        { name: "Contact Us", path: "/contact-us" },
      ],
    },
    {
      category: "Banking & Crypto",
      icon: <FaStore className="text-emerald-400" />,
      color: "emerald",
      links: [
        { name: "Zero-Coin", path: "/zero-coin" },
        { name: "Crypto Store", path: "/crypto-store" },
        { name: "Banking", path: "/banking" },
        { name: "Wallet", path: "/wallet" },
        { name: "ICO", path: "/ico" },
      ],
    },
    {
      category: "User Features",
      icon: <FaUser className="text-blue-400" />,
      color: "blue",
      links: [
        { name: "Dashboard", path: "/dashboard" },
        { name: "Investments", path: "/investments" },
        { name: "Portfolio", path: "/portfolio" },
        { name: "Governance", path: "/governance" },
        { name: "NFT Marketplace", path: "/nft-marketplace" },
      ],
    },
    {
      category: "Company Information",
      icon: <FaBuilding className="text-purple-400" />,
      color: "purple",
      links: [
        { name: "Careers", path: "/careers" },
        { name: "Partners", path: "/partners" },
        { name: "Blog", path: "/blog" },
        { name: "Support", path: "/support" },
        { name: "Market Data", path: "/market-data" },
      ],
    },
    {
      category: "Technical Resources",
      icon: <FaCode className="text-orange-400" />,
      color: "orange",
      links: [
        { name: "Developers", path: "/developers" },
        { name: "Tokenomics", path: "/tokenomics" },
        { name: "Compliance", path: "/compliance" },
        { name: "Roadmap", path: "/roadmap" },
      ],
    },
    {
      category: "Legal",
      icon: <FaGavel className="text-gray-300" />,
      color: "gray",
      links: [
        { name: "Privacy Policy", path: "/privacy-policy" },
        { name: "Terms of Use", path: "/terms-of-use" },
        { name: "Sales Policy", path: "/sales-policy" },
        { name: "Legal", path: "/legal" },
      ],
    },
  ];

  const colorClasses = {
    cyan: {
      border: "group-hover:border-cyan-500/50",
      glow: "group-hover:shadow-cyan-500/20",
      bg: "from-cyan-500/0 via-cyan-500/5",
      accent: "bg-cyan-500",
      text: "group-hover:text-cyan-400",
    },
    emerald: {
      border: "group-hover:border-emerald-500/50",
      glow: "group-hover:shadow-emerald-500/20",
      bg: "from-emerald-500/0 via-emerald-500/5",
      accent: "bg-emerald-500",
      text: "group-hover:text-emerald-400",
    },
    blue: {
      border: "group-hover:border-blue-500/50",
      glow: "group-hover:shadow-blue-500/20",
      bg: "from-blue-500/0 via-blue-500/5",
      accent: "bg-blue-500",
      text: "group-hover:text-blue-400",
    },
    purple: {
      border: "group-hover:border-purple-500/50",
      glow: "group-hover:shadow-purple-500/20",
      bg: "from-purple-500/0 via-purple-500/5",
      accent: "bg-purple-500",
      text: "group-hover:text-purple-400",
    },
    orange: {
      border: "group-hover:border-orange-500/50",
      glow: "group-hover:shadow-orange-500/20",
      bg: "from-orange-500/0 via-orange-500/5",
      accent: "bg-orange-500",
      text: "group-hover:text-orange-400",
    },
    gray: {
      border: "group-hover:border-gray-500/50",
      glow: "group-hover:shadow-gray-500/20",
      bg: "from-gray-500/0 via-gray-500/5",
      accent: "bg-gray-500",
      text: "group-hover:text-gray-300",
    },
  };

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50">
      {/* Hero Section - Full Viewport Height */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/sitemap-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="siteMapHeroSparkles"
              background="transparent"
              minSize={0.4}
              maxSize={1.0}
              particleDensity={30}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Site Map"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              The Blueprint of the Massive 12-Brand Alliance
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          additionalClasses=""
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-10 py-20 mx-auto max-w-7xl">
        {/* Introduction */}
        <div className="mb-16 text-center">
          <h2 className="text-4xl font-bold bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
            Explore Our Site
          </h2>
          <p className="mt-4 text-gray-300 max-w-2xl mx-auto">
            Navigate through all sections of our platform. Click any link to explore.
          </p>
        </div>

        {/* Site Map Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {siteMapLinks.map((section, index) => {
            const colors = colorClasses[section.color];
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                ref={(el) => (sectionRefs.current[index] = el)}
                className={`group relative p-[1px] rounded-2xl overflow-hidden transition-all duration-500 ${colors.glow} hover:shadow-xl`}
              >
                {/* Animated gradient border */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-white/5 via-white/10 to-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                {/* Inner card */}
                <div className={`relative bg-black/90 backdrop-blur-sm rounded-2xl p-6 h-full border border-white/10 ${colors.border} transition-all duration-500`}>
                  {/* Spotlight effect */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-radial from-white/5 to-transparent rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  {/* Category Header */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className="p-2 rounded-lg bg-white/5 border border-white/10">
                      {section.icon}
                    </div>
                    <h3 className="text-lg font-bold text-white">{section.category}</h3>
                  </div>

                  {/* Links */}
                  <ul className="space-y-1">
                    {section.links.map((link, idx) => (
                      <motion.li
                        key={idx}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 + idx * 0.05, duration: 0.3 }}
                      >
                         <GlassButton
                           onClick={() => handleNavigate(link.path)}
                           variant={section.color === "gray" ? "gray" : section.color}
                           size="sm"
                           className="w-full text-left justify-between"
                         >
                           <div className="flex items-center justify-between w-full group/link">
                             <span className="flex items-center gap-2">
                               {link.name}
                             </span>
                             <TiArrowRight className="opacity-0 group-hover/link:opacity-100 group-hover/link:translate-x-1 transition-all duration-300" />
                           </div>
                         </GlassButton>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Back to Top Button */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-cyan-500/30 rounded-full shadow-lg shadow-cyan-500/10 cursor-pointer hover:bg-black/60 hover:border-cyan-400/50 transition-all duration-500 z-50"
          onClick={scrollToTop}
          aria-label="Back to Top"
        >
          <TiArrowUp className="text-cyan-400 transition-all duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider transition-colors duration-500 hover:text-white">BACK TO TOP</span>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SiteMap;

